import OverflowMarquee from './OverflowMarquee';

function getCellValue(tableData, row, col, transpose) {
  if (transpose) {
    return tableData.get(col)?.get(row) || '';
  }

  return tableData.get(row)?.get(col) || '';
}

function getSubjectTagOverride(subject, selections) {
  const normalizedSubject = String(subject || '').trim().toLocaleLowerCase();
  const selection = Object.entries(selections).find(([selectionSubject]) =>
    selectionSubject.trim().toLocaleLowerCase() === normalizedSubject)?.[1];

  return String(selection?.tag_name || '').trim();
}

function HomeworkTable({
  tableData,
  maxRow,
  maxCol,
  transpose = false,
  fitToWidth = false,
  fillAvailableHeight = false,
  dateLabel = '',
  dateTime = '',
  subjectTags = new Map(),
  subjectTagSelections,
}) {
  if (!tableData) {
    return (
      <div className={`flex items-center justify-center gap-3 text-gray-700 ${fillAvailableHeight ? 'min-h-0 flex-1' : 'py-12'}`}>
        <svg
          className="h-5 w-5 animate-spin text-blue-600"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
        <span className="text-base font-medium">Loading...</span>
      </div>
    );
  }

  const rowCount = transpose ? maxCol : maxRow;
  const colCount = transpose ? maxRow : maxCol;
  const scrollContainerClass = fitToWidth ? 'w-full' : 'w-full overflow-x-auto';
  const tableClass = fitToWidth
    ? `w-full table-fixed border-collapse text-left text-[#f7f4e8] ${fillAvailableHeight ? 'h-full' : ''}`
    : 'w-full min-w-[42rem] table-fixed border-collapse text-left text-[2.75rem] text-[#f7f4e8] sm:text-5xl';
  const cellClass = fitToWidth
    ? 'overflow-hidden border border-[#dfe7df] px-[clamp(0.25rem,1vw,0.75rem)] py-1 align-middle font-normal leading-none'
    : 'min-w-32 border border-[#dfe7df] px-6 py-8 align-top font-normal leading-tight break-words sm:px-8';
  const responsiveFontSize = `clamp(1rem, min(5.25vw, ${52 / Math.max(rowCount, 1)}dvh), 5.5rem)`;

  return (
    <section className={`${fillAvailableHeight ? 'min-h-0 flex-1' : ''} flex w-full flex-col`} aria-label="Homework table">
      <div className={`${fillAvailableHeight ? 'h-full min-h-0' : ''} flex flex-col overflow-hidden rounded-sm border-4 border-[#cbd2cc] bg-[#103a33] shadow-md ring-1 ring-gray-500`}>
        {dateLabel && (
          <div className="shrink-0 border-b border-[#dfe7df] bg-[#173f38] px-3 py-[clamp(0.25rem,0.8dvh,0.75rem)] text-center text-[clamp(1rem,2.2vw,1.75rem)] font-semibold leading-tight text-[#f7f4e8]">
            <time dateTime={dateTime || dateLabel}>{dateLabel}</time>
          </div>
        )}
        <div className={`${scrollContainerClass} ${fillAvailableHeight ? 'min-h-0 flex-1' : ''}`}>
          <table
            className={`${tableClass} ${fitToWidth ? 'homework-table' : ''}`}
            style={fitToWidth ? { '--homework-table-font-size': responsiveFontSize } : undefined}
          >
            <tbody>
              {Array.from({ length: rowCount }, (_, rowIndex) => {
                const row = rowIndex + 1;
                const isHeaderRow = row === 1;

                return (
                  <tr
                    key={row}
                    className={isHeaderRow ? 'bg-[#173f38]' : 'bg-[#103a33]'}
                    style={fillAvailableHeight ? { height: `${100 / Math.max(rowCount, 1)}%` } : undefined}
                  >
                    {Array.from({ length: colCount }, (_, colIndex) => {
                      const col = colIndex + 1;
                      const value = getCellValue(tableData, row, col, transpose);
                      const Cell = isHeaderRow ? 'th' : 'td';
                      const isSubjectCell = !transpose && !isHeaderRow && col === 1;
                      const isHomeworkCell = !transpose && !isHeaderRow && col === 2;
                      const subjectTag = isSubjectCell
                        ? (subjectTagSelections === undefined
                          ? subjectTags.get(row)
                          : getSubjectTagOverride(value, subjectTagSelections))
                        : '';

                      return (
                        <Cell
                          key={col}
                          scope={isHeaderRow ? 'col' : undefined}
                          className={`${cellClass} text-center ${colIndex === 0 ? 'font-semibold' : ''}`}
                        >
                          {subjectTag ? (
                            <span className="flex min-w-0 items-end justify-center gap-[clamp(0.25rem,0.8vw,0.75rem)] overflow-hidden whitespace-nowrap">
                              <span className="min-w-0 truncate">{value}</span>
                              <span className="inline-flex max-w-[45%] shrink items-center rounded-full border border-[#aebbb6] px-[clamp(0.25rem,0.65vw,0.6rem)] py-[0.15em] text-[clamp(0.55rem,1.15vw,0.9rem)] font-semibold leading-none text-[#dfe7df]">
                                <span className="truncate">{subjectTag}</span>
                              </span>
                            </span>
                          ) : isHomeworkCell && value ? (
                            <OverflowMarquee>{value}</OverflowMarquee>
                          ) : (
                            <span className="block overflow-hidden text-ellipsis whitespace-nowrap" title={value || undefined}>
                              {value}
                            </span>
                          )}
                        </Cell>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default HomeworkTable;
